/**
 * Wraps every authenticated route. Calls ``useCurrentUser`` and redirects
 * the browser based on the resolved status. The server is still the
 * authority on each `/api/*` call — this layer exists purely to prevent
 * a flash of authenticated chrome before the server's 401 lands.
 */

import { type PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Spin } from "antd";

import { type MeResponse } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";

/** A scoped, off-ladder role (e.g. POS): holds the `pos` capability but not the
 * hierarchical `view` base. Such a user gets a menu-less shell on /pos and is
 * kept out of the full app shell. Owner/superadmin hold `view` too → not scoped. */
function isPosOnly(user: MeResponse | null): boolean {
  const caps = user?.capabilities ?? [];
  return caps.includes("pos") && !caps.includes("view");
}

export const ProtectedRoute = ({ children }: PropsWithChildren) => {
  const { status, user } = useCurrentUser();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }
  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (status === "first-launch") {
    return <Navigate to="/setup" replace />;
  }
  if (status === "pending") {
    return <Navigate to="/pending-approval" replace />;
  }
  if (status === "error") {
    // Fail open to /login so the user sees a clear "sign in" path rather
    // than an opaque spinner. The auth_event log captures the underlying
    // error if it was network/server-side.
    return <Navigate to="/login" replace />;
  }
  // A scoped point-of-sale user only ever sees the menu-less /pos surface.
  if (isPosOnly(user) && location.pathname !== "/pos") {
    return <Navigate to="/pos" replace />;
  }
  return <>{children}</>;
};
